import React, { useState } from "react";
import {
  ShieldCheck,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Download,
  Users,
  FlaskConical,
  GraduationCap,
  Code2,
  Sparkles,
  FileJson,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { MoreTools } from "@/components/MoreTools";
import { Seo } from "@/components/Seo";

const faqs = [
  {
    q: "What is a fake patient generator?",
    a: "A fake patient generator creates synthetic patient records — names, dates of birth, member IDs, group numbers, SSNs, addresses, and phone numbers — that look realistic but belong to no real person. Hewto.ai's generator runs entirely in your browser and is built for healthcare software development, QA, training, and demos.",
  },
  {
    q: "Are the generated patient records real?",
    a: "No. Every record is randomly assembled from name lists, never-issued SSN ranges (area 900–999), and fictional phone numbers (the 555-01xx range reserved for fiction). No record corresponds to a real person, so the data is safe for non-production use.",
  },
  {
    q: "What fields does each fake patient record include?",
    a: "Each record includes first name, last name, gender, date of birth and age, member ID, group number, a synthetic SSN from the never-issued 9xx range, street address, city, state, ZIP code, and a fictional 555-01xx phone number — the fields a CMS-1500 or 837P claim needs for the subscriber and patient loops.",
  },
  {
    q: "What should I use fake patient data for?",
    a: "Use it anywhere you need realistic patient data without PHI: software development, QA and regression testing, 837 / EDI file generation tests, clearinghouse onboarding, staff training, product demos, and populating sandbox databases. Pair it with our Fake NPI Generator for complete test claims.",
  },
  {
    q: "Can I use fake patient data on a real claim?",
    a: "Never. Submitting claims with fabricated patient or subscriber data is fraud. Synthetic patient records are strictly for test, development, training, and demo environments — never for production claims or any HIPAA-covered transaction.",
  },
  {
    q: "Why does using synthetic data matter for HIPAA?",
    a: "HIPAA applies to real protected health information. Using synthetic patient records in development, testing, and training keeps real PHI out of non-production systems, reducing breach exposure and simplifying compliance — a practice recommended across the healthcare IT industry.",
  },
  {
    q: "Is this fake patient generator free?",
    a: "Yes — 100% free, no signup, no API key. Generate up to 100 synthetic patient records per batch, copy them as JSON, or export to CSV in one click.",
  },
  {
    q: "Do the generated SSNs collide with real ones?",
    a: "No. The Social Security Administration has never issued area numbers in the 900–999 range, so every generated SSN (formatted 9XX-XX-XXXX) is guaranteed to be unassigned. Combined with fictional names and 555 phone numbers, records are safe for testing.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name: "Hewto.ai Fake Patient Generator",
  applicationCategory: "DeveloperApplication",
  operatingSystem: "Web",
  browserRequirements: "Requires JavaScript",
  description:
    "Free fake patient generator — creates synthetic patient records (name, DOB, member ID, group number, SSN, address, phone) for healthcare software development, QA, training, and demos. Bulk up to 100 with JSON and CSV export.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

const FIRST_MALE = [
  "James", "Robert", "Michael", "William", "David", "Richard", "Joseph", "Thomas", "Charles",
  "Daniel", "Matthew", "Anthony", "Mark", "Donald", "Steven", "Paul", "Andrew", "Kenneth",
];
const FIRST_FEMALE = [
  "Mary", "Patricia", "Jennifer", "Linda", "Barbara", "Elizabeth", "Susan", "Jessica", "Sarah",
  "Karen", "Nancy", "Lisa", "Margaret", "Betty", "Sandra", "Ashley", "Dorothy", "Kimberly",
];
const LAST_NAMES = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez",
  "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas", "Taylor", "Moore",
];
const STREETS = [
  "Main St", "Oak Ave", "Maple Dr", "Cedar Ln", "Pine St", "Elm St", "Washington Ave",
  "Lake Rd", "Hill Ct", "Park Blvd", "Sunset Dr", "River Rd",
];
const CITIES = [
  { city: "Springfield", state: "IL", zip: "62701" },
  { city: "Austin", state: "TX", zip: "73301" },
  { city: "Columbus", state: "OH", zip: "43004" },
  { city: "Charlotte", state: "NC", zip: "28201" },
  { city: "Phoenix", state: "AZ", zip: "85001" },
  { city: "Denver", state: "CO", zip: "80201" },
  { city: "Nashville", state: "TN", zip: "37201" },
  { city: "Madison", state: "WI", zip: "53701" },
];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const pad = (n, l) => String(n).padStart(l, "0");
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

const generatePatient = (genderFilter) => {
  const gender =
    genderFilter === "any" ? (Math.random() < 0.5 ? "Male" : "Female") : genderFilter;
  const firstName = gender === "Male" ? rand(FIRST_MALE) : rand(FIRST_FEMALE);
  const lastName = rand(LAST_NAMES);
  const dobYear = randInt(1935, 2015);
  const dob = `${dobYear}-${pad(randInt(1, 12), 2)}-${pad(randInt(1, 28), 2)}`;
  const age = new Date().getFullYear() - dobYear;
  const loc = rand(CITIES);
  return {
    first_name: firstName,
    last_name: lastName,
    gender,
    dob,
    age,
    member_id: `MB${pad(randInt(0, 999999999), 9)}`,
    group_number: `GRP${pad(randInt(0, 999999), 6)}`,
    ssn: `9${pad(randInt(0, 99), 2)}-${pad(randInt(1, 99), 2)}-${pad(randInt(1, 9999), 4)}`,
    phone: `(${randInt(200, 989)}) 555-01${pad(randInt(0, 99), 2)}`,
    address: `${randInt(100, 9999)} ${rand(STREETS)}`,
    city: loc.city,
    state: loc.state,
    zip: loc.zip,
  };
};

const generateBatch = (count, genderFilter) => {
  const seen = new Set();
  const out = [];
  while (out.length < count) {
    const p = generatePatient(genderFilter);
    const key = `${p.first_name}|${p.last_name}|${p.dob}|${p.member_id}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(p);
    }
  }
  return out;
};

const CSV_FIELDS = [
  "first_name", "last_name", "gender", "dob", "age", "member_id", "group_number",
  "ssn", "phone", "address", "city", "state", "zip",
];

const COUNT_OPTIONS = [1, 5, 10, 25, 50, 100];
const GENDER_OPTIONS = [
  { value: "any", label: "Any gender" },
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const FakePatientGenerator = () => {
  const [count, setCount] = useState(10);
  const [gender, setGender] = useState("any");
  const [results, setResults] = useState([]);

  const runGenerate = () => {
    setResults(generateBatch(count, gender));
    toast.success(`Generated ${count} fake patient${count === 1 ? "" : "s"}`);
  };

  const copyAllJson = () => {
    navigator.clipboard.writeText(JSON.stringify(results, null, 2));
    toast.success(`${results.length} records copied as JSON`);
  };

  const copyOne = (p) => {
    navigator.clipboard.writeText(JSON.stringify(p, null, 2));
    toast.success(`${p.first_name} ${p.last_name} copied as JSON`);
  };

  const exportCsv = () => {
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [CSV_FIELDS.join(",")];
    for (const p of results) lines.push(CSV_FIELDS.map((f) => esc(p[f])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fake-patients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageTransition>
      <Seo
        title="Fake Patient Generator — Synthetic Patient Data for Testing | Hewto.ai"
        description="Free fake patient generator for healthcare developers and QA teams. Generate synthetic patient records — name, DOB, member ID, group number, never-issued SSNs, addresses, fictional phone numbers — for testing, training, demos, and 837/EDI development. Bulk up to 100, JSON & CSV export, no signup."
        keywords="fake patient generator, synthetic patient data, fake patient data, test patient records, synthetic patient records, dummy patient data, patient test data generator, mock patient data, sample patient records, healthcare test data, phi-free test data, synthetic health data, 837 test data, edi test patients, fake patient names, test data for claims, hipaa compliant test data, qa test data healthcare, patient mock data generator, free synthetic patient generator"
        canonical="https://www.hewto.ai/tools/fake-patient-generator"
        jsonLd={[faqJsonLd, softwareJsonLd]}
      />

      <PageHero
        eyebrow="Free tool · Fake Patient Generator"
        title="Fake Patient Generator for Testing"
        subtitle="Generate synthetic patient records in one click — realistic names, dates of birth, member IDs, group numbers, never-issued SSNs, addresses, and fictional phone numbers for development, QA, training, and demos. Bulk up to 100 with JSON and CSV export. Free, no signup, nothing stored."
      />

      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-brand/5 sm:p-8">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="fake-patient-disclaimer">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm leading-relaxed text-amber-800">
                <span className="font-semibold">For testing only.</span> These records are randomly
                generated and belong to no real person. Never use them on a real claim or any
                HIPAA-covered transaction.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-700">How many patients?</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="fake-patient-count-group">
                  {COUNT_OPTIONS.map((c) => (
                    <button
                      key={c}
                      data-testid={`fake-patient-count-${c}`}
                      onClick={() => setCount(c)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        count === c
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="fake-patient-gender" className="text-sm font-semibold text-slate-700">
                  Gender
                </label>
                <select
                  id="fake-patient-gender"
                  data-testid="fake-patient-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none"
                >
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              data-testid="fake-patient-generate-btn"
              onClick={runGenerate}
              className="mt-6 h-12 w-full rounded-xl bg-brand px-6 text-base font-semibold text-white hover:bg-brand-600"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Generate fake patients
            </Button>
            <p className="mt-2 text-xs text-slate-500">
              Generated in your browser — nothing is sent to or stored on our servers.
            </p>

            {results.length > 0 && (
              <div className="mt-6" data-testid="fake-patient-results">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-700">
                    {results.length} synthetic patient record{results.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      data-testid="fake-patient-copy-all-btn"
                      variant="outline"
                      onClick={copyAllJson}
                      className="rounded-full"
                    >
                      <FileJson className="mr-2 h-4 w-4" /> Copy JSON
                    </Button>
                    <Button
                      data-testid="fake-patient-csv-btn"
                      variant="outline"
                      onClick={exportCsv}
                      className="rounded-full"
                    >
                      <Download className="mr-2 h-4 w-4" /> Export CSV
                    </Button>
                  </div>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="max-h-[520px] overflow-auto">
                    <table data-testid="fake-patient-table" className="w-full min-w-[960px] text-left text-sm">
                      <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="p-3">Name</th>
                          <th className="p-3">Gender</th>
                          <th className="p-3">DOB (Age)</th>
                          <th className="p-3">Member ID</th>
                          <th className="p-3">Group</th>
                          <th className="p-3">SSN</th>
                          <th className="p-3">Phone</th>
                          <th className="p-3">City, State</th>
                          <th className="p-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((p) => (
                          <tr key={p.member_id} className="border-t border-slate-100" data-testid={`fake-patient-row-${p.member_id}`}>
                            <td className="p-3 font-medium text-slate-900">
                              {p.first_name} {p.last_name}
                            </td>
                            <td className="p-3 text-slate-600">{p.gender}</td>
                            <td className="p-3 font-mono text-xs text-slate-700">
                              {p.dob} <span className="text-slate-400">({p.age})</span>
                            </td>
                            <td className="p-3 font-mono text-xs text-slate-700">{p.member_id}</td>
                            <td className="p-3 font-mono text-xs text-slate-700">{p.group_number}</td>
                            <td className="p-3 font-mono text-xs text-slate-700">{p.ssn}</td>
                            <td className="p-3 font-mono text-xs text-slate-700">{p.phone}</td>
                            <td className="p-3 text-slate-600">
                              {p.city}, {p.state}
                            </td>
                            <td className="p-3">
                              <button
                                data-testid={`fake-patient-copy-${p.member_id}`}
                                onClick={() => copyOne(p)}
                                title="Copy record as JSON"
                                className="text-slate-300 transition-colors hover:text-brand"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Under the hood
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Built to be realistic — and provably fake
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Every field is chosen so records behave like real patient data in your forms and
                pipelines, while being mathematically impossible to confuse with real people:
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                {[
                  "SSNs use the 900–999 area range the SSA has never issued — guaranteed unassigned",
                  "Phone numbers use the 555-01xx range reserved for fiction",
                  "Member IDs and group numbers follow common payer formats",
                  "Names drawn from common US first/last name lists, matched to gender",
                  "Uniqueness guaranteed within every batch",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-slate-950 p-6 shadow-lg">
              <pre className="whitespace-pre text-xs leading-relaxed text-slate-200">
{`{
  "first_name": "James",
  "last_name": "Smith",
  "gender": "Male",
  "dob": "1962-04-17",
  "member_id": "MB482017365",
  "group_number": "GRP012847",
  "ssn": "900-42-1177",
  "phone": "(312) 555-0148",
  "address": "1042 Maple Dr",
  "city": "Springfield",
  "state": "IL",
  "zip": "62701"
}`}
              </pre>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Pair with our{" "}
              <a href="/tools/fake-npi-generator" className="font-semibold text-brand hover:underline">
                Fake NPI Generator
              </a>{" "}
              to assemble complete synthetic CMS-1500 or 837P test claims — patient, subscriber, and
              provider identifiers, all fake, all realistic.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY SYNTHETIC DATA */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Sparkles className="h-3.5 w-3.5" /> Why synthetic data
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Realistic patient data, zero PHI risk
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: Code2,
              title: "Development & QA",
              desc: "Populate dev and staging databases, test claim intake and 837 generation, and run clearinghouse onboarding without a single real patient record.",
            },
            {
              icon: FlaskConical,
              title: "Pipeline testing",
              desc: "Feed realistic subscriber and patient demographics through extraction, validation, and adjudication pipelines so tests behave exactly like production.",
            },
            {
              icon: GraduationCap,
              title: "Training & demos",
              desc: "Teach billing staff and demo products with realistic-looking patient records — keeping PHI out of classrooms, screenshots, and slide decks.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p className="text-sm leading-relaxed text-slate-600">
              Building a full test claim? Generate the patient here, grab a rendering and billing
              NPI from the{" "}
              <a href="/tools/fake-npi-generator" className="font-semibold text-brand hover:underline">
                Fake NPI Generator
              </a>
              , and validate any real NPIs with the{" "}
              <a href="/tools/npi-validator" className="font-semibold text-brand hover:underline">
                NPI Validator
              </a>
              .
            </p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            Fake patient generator FAQ
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Common questions about synthetic patient data
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8" data-testid="fake-patient-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  data-testid={`fake-patient-faq-trigger-${i}`}
                  className="text-left text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <MoreTools current="/tools/fake-patient-generator" />

      <CTABand />
    </PageTransition>
  );
};

export default FakePatientGenerator;
